import {
  Box,
  Card,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState } from 'src/redux-store/appStore';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Fields } from 'src/ui-component/FormikFormLibrary/FormikTypes';
import '../../../../../App.css';
import CustomModal from 'src/ui-component/customModal/CustomModal';
import CustomButtonContained from 'src/components/CustomButton/CustomButtonContained';
import CustomButtonOutlined from 'src/components/CustomButton/CustomButtonOutlined';
import ContentOnReplacementMethodChange from './ReplacementMethods';
import { format } from 'date-fns';
import { shouldShowEdit } from 'src/utils/CommonFunctions';
import DownloadPopUp from 'src/ui-component/DownloadApplicationForm/DownloadPopUp';

function EditApplicationForManager({
  handleSubmitOnUpdateAndPayment,
  handleBlockLotsList,
  blockLotsOptions,
  setSwitchTo,
  treePermitFormData,
  setTreePermitFormData,
  applicationData,
  replacementMethodData,
  setReplacementMethodData,
  handleApproveDeny,
  setClickedOn
}) {

  const navigate = useNavigate();
  const messageBodyRef = useRef<string>('');
  const { townshipId } = useParams();
  const currentPermitDetails = useSelector(
    (state: RootState) => state?.currentPermit?.currentPermitDetails
  );
  const currentTownshipDetails = useSelector(
    (state: RootState) => state?.township?.townshipDetails
  );
  const basePath = `/${currentPermitDetails.townshipId}/${currentPermitDetails.departmentName}/${currentPermitDetails.id}`;
  const isManager = currentPermitDetails.currentPermitUserRole === 'Manager';
  const [openApprovePopup, setOpenApprovePopup] = useState<boolean>(false);
  const [conditionChecks, setConditionChecks] = useState({} as any);
  const [IsOpen, setIsOpen] = useState<boolean>(false);
  const [replacementFee, setReplacementFee] = useState<number>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (applicationData) {
      if (
        applicationData?.MethodOfTreeReplacement ===
          'TownshipTreeReplacement' ||
        applicationData?.SelectionOption === 'TownshipTreeReplacement'
      ) {
        setReplacementMethodData({
          vendorToReplace: '',
          townshipToReplace: 'yes'
        });
      } else if (
        applicationData?.MethodOfTreeReplacement === 'VendorTreeReplacement' ||
        applicationData?.SelectionOption === 'VendorTreeReplacement' ||
        applicationData?.SelectionOption === 'ReplacementFeePaid'
      ) {
        setReplacementMethodData({
          vendorToReplace: 'yes',
          townshipToReplace: ''
        });
      }
    }
  }, [applicationData]);

  const handleUserMessage = () => {
    let message;
    const check =
      applicationData?.SelectionOption === 'TownshipTreeReplacement' &&
      applicationData?.ApplicationStatus === 'Await CO';
    if (applicationData?.MethodOfTreeReplacement) {
      message = "Waiting for Vendor's response.";
    } else if (applicationData?.SelectionOption) {
      const msg =
        applicationData?.ApplicationStatus === 'Approved'
          ? `This application has been successfully approved ${
              applicationData?.ApprovedDate &&
              ` on ${format(
                new Date(applicationData?.ApprovedDate),
                'MM/dd/yyyy'
              )}`
            }.`
          : applicationData?.ApplicationStatus === 'Rejected'
          ? `This application has been rejected ${
              applicationData?.RejectionDate !== null
                ? ` on ${format(
                    new Date(applicationData?.RejectionDate),
                    'MM/dd/yyyy'
                  )}`
                : ''
            }.`
          : (applicationData?.SelectionOption === 'TownshipTreeReplacement' ||
              applicationData?.SelectionOption === 'VendorTreeReplacement') &&
            applicationData?.SelectionOption !== 'ReplacementFeePaid'
          ? `${check ? 'Township' : 'Vendor'} agree to replace ${
              check ? `${applicationData?.ReplaceTrees} tree(s).` : '.'
            }`
          : `$${
              applicationData?.AdditionalEscrowFee || 0
            } replacement fee paid by vendor.`;
      message = msg;
    }
    return message;
  };

  // Function to handle the message change
  const handleMessageChange = (e) => {
    messageBodyRef.current = e.target.value;
  };

  // Function to handle the form fields change
  const handleChange = (val, e) => {
    const { name } = e;
    if (name === 'blockLot') handleBlockLotsList(val);
    else
      setTreePermitFormData({
        ...treePermitFormData,
        [name]: val
      });
  };

  // Form Fields using Formik
  const formFields: Fields[] = [
    {
      fieldName: 'blockLot',
      fieldLabel: 'Block Lot OR Address where tree(s) are located',
      fieldType: 'autocomplete',
      placeHolder: 'Enter minimum 3 digits',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.blockLot,
      disabled: !isManager,
      options: blockLotsOptions || [
        {
          label: treePermitFormData?.blockLot,
          BlockLot: treePermitFormData?.blockLot,
          Owner: treePermitFormData?.owner,
          PropAddress: treePermitFormData?.treeAddress
        }
      ],
      dependency: handleChange
    },
    {
      fieldName: 'owner',
      fieldLabel: 'Owner',
      fieldType: 'text',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.owner?.trim(),
      disabled: false,
      dependency: handleChange
    },
    {
      fieldName: 'treeAddress',
      fieldLabel: 'Address where tree(s) are located',
      fieldType: 'note',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.treeAddress?.trim(),
      disabled: false,
      dependency: handleChange
    },
    {
      fieldName: 'nearBy',
      fieldLabel: 'Nearest Cross Street',
      fieldType: 'note',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.nearBy?.trim(),
      disabled: false,
      dependency: handleChange
    },
    {
      fieldName: 'phone',
      fieldLabel: 'Phone Number',
      fieldType: 'mobile',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.phone || '',
      disabled: false,
      dependency: handleChange
    },
    {
      fieldName: 'numberOfTrees',
      fieldLabel: 'Number of Trees to be Removed',
      fieldType: 'number',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.numberOfTrees,
      lengthLimit: 3,
      disabled: false,
      dependency: handleChange
    },
    {
      fieldName: 'purpose',
      fieldLabel: 'Purpose of Removal',
      fieldType: 'note',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.purpose?.trim(),
      disabled: false,
      dependency: handleChange
    },
    {
      fieldName: 'lotSize',
      fieldLabel: 'Lot Size (S.F./ Acreage)',
      fieldType: 'note',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.lotSize?.trim(),
      disabled: false,
      dependency: handleChange
    },
    {
      fieldName: 'areaConfirmationCheck',
      fieldLabel:
        'Are trees being removed from a restricted area? (i.e. Wetlands, Easements, Buffers, etc.)',
      fieldType: 'radio',
      isErrorField: false,
      required: true,
      initialValue: treePermitFormData?.areaConfirmationCheck,
      options: [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
        { value: 'Unsure', label: 'Unsure' }
      ],
      disabled: false,
      dependency: handleChange
    }
  ];

  const townshipTreeReplacement = [
    {
      fieldName: 'condition',
      fieldLabel: 'Condition',
      fieldType: 'note',
      isErrorField: false,
      required: true,
      br: true,
      initialValue: treePermitFormData?.conditions,
      disabled: !isManager
    },
    {
      fieldName: 'inspectionDate',
      fieldLabel: 'Inspection Date',
      fieldType: 'futureDate',
      isErrorField: false,
      required: true,
      br: true,
      initialValue: treePermitFormData?.inspectionDate,
      disabled: !isManager
    }
  ];

  const replacementMethodContent = [
    { name: 'vendorToReplace', text: 'Require Vendor to replace the tree?' },
    { name: 'townshipToReplace', text: 'Require Township to replace the tree?' }
  ];

  const getFormButtons = () => {
    let buttonsCollection = ['Cancel'];
    if (applicationData?.ApplicationStatus) {
      if (applicationData?.ApplicationStatus === 'Draft') {
        buttonsCollection = [...buttonsCollection, 'Update'];
      }
      if (applicationData?.ApplicationStatus === 'Draft') {
        buttonsCollection = [...buttonsCollection, 'Proceed to Payment'];
      }
    }
    return buttonsCollection;
  };

  return (
    <>
      <Box>
        <Card sx={{ p: { xs: 1.5, sm: 3 }, pt: 0 }}>
          <Box sx={{ mt: 1 }} className="newApp">
            <Box
              pb={2}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                alignContent: 'center'
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    name={'ToggleApplicationDetails'}
                    color="primary"
                    onChange={(e) =>
                      setConditionChecks({
                        ...conditionChecks,
                        ToggleApplicationDetails: e.target.checked
                      })
                    }
                  />
                }
                label={'Hide/Show Application Details'}
              />
              <Box>
                <DownloadPopUp
                  title={'Tree Removal Application'}
                  ApplicationDetails={{
                    BlockLot: treePermitFormData?.blockLot,
                    FullName: treePermitFormData?.owner?.trim(),
                    AddressWhereTreeAreLocated:
                      treePermitFormData?.treeAddress?.trim(),
                    NearestCrossStreet: treePermitFormData?.nearBy?.trim(),
                    phoneNumber: treePermitFormData?.phone,
                    NumberOfTreesToBeRemoved: treePermitFormData?.numberOfTrees,
                    'Lot Size (S.F./ Acreage)':
                      treePermitFormData?.lotSize?.trim(),
                    PurposeOfRemoval: treePermitFormData?.purpose?.trim(),
                    'Are trees being removed from a restricted area? (i.e. Wetlands, Easements, Buffers, etc.)':
                      treePermitFormData?.areaConfirmationCheck,
                    'I have read and understand Township Code Chapter 457 Trees and Invasive Species.':
                      <Checkbox />,
                    'I understand that the Tree Removal Contractor I will retain is registered.':
                      <Checkbox />,
                    'All trees to be removed must be field marked prior to submitting your application.':
                      <Checkbox />
                  }}
                />
              </Box>
            </Box>

            {conditionChecks?.ToggleApplicationDetails ? (
              <FormikForm
                allFields={[...formFields]}
                buttonTitle={getFormButtons()}
                formMethod={handleSubmitOnUpdateAndPayment}
                setClickedOn={setClickedOn}
                defaultEnabledButton={['Proceed to Payment', 'Approve']}
                clickProp
                buttonPlacement={'right'}
                handleOtherButton={() => navigate(`/dashboard/${townshipId}`)}
              />
            ) : null}
          </Box>
          <Grid item xs={12} container alignItems="center" spacing={2}>
            <Grid item xs={12}>
              <CustomButtonOutlined
                startIcon={<CloudUploadIcon />}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  mt: { xs: 2, sm: 'auto' }
                }}
                title={'Upload/View Document'}
                onClick={() => {
                  setSwitchTo('Upload/View Document');
                }}
              />
            </Grid>
          </Grid>
          {applicationData?.ApplicationStatus !== 'Draft' && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h4" mb={3} color={'#6e759f'}>
                {`${currentTownshipDetails?.name?.toUpperCase()} TREE REPLACEMENT SELECTION FORM`}
              </Typography>
              <FormikForm
                allFields={[...townshipTreeReplacement]}
                buttonTitle={
                  isManager
                    ? ['Update Condition / Inspection Date']
                    : ['Return To Home ', 'Cancel', 'Update']
                }
                setButtonExternallyDisabled={
                  shouldShowEdit(applicationData)
                    ? 'Update Condition / Inspection Date'
                    : null
                }
                formMethod={handleSubmitOnUpdateAndPayment}
                buttonPlacement={'right'}
                alignmentDirection={'column'}
                setClickedOn={setClickedOn}
                clickProp
                handleOtherButton={() => {
                  if (applicationData?.ApplicationStatus === 'Draft')
                    navigate(`${basePath}/draft-application`);
                  else navigate(`${basePath}/application-list`);
                }}
              />
              <Divider sx={{ mt: 2 }} />
            </>
          )}

          <>
            <Typography
              mb={1}
              mt={2}
              color={
                applicationData?.ApplicationStatus === 'Rejected'
                  ? '#8B0000'
                  : 'green'
              }
              fontSize={15}
              fontWeight={500}
            >
              {handleUserMessage()}
            </Typography>
            {applicationData &&
              applicationData?.SelectionOption === 'VendorTreeReplacement' &&
              applicationData?.ApplicationStatus === 'Await CO' &&
              JSON.parse(applicationData?.ReplacementTreeCaliper)?.map(
                (detail, index) => {
                  return (
                    <Typography color={'#858796'} my={1} key={index}>
                      <b className="theme_text_color">{`(${detail?.Trees})`}</b>{' '}
                      tree(s) at{' '}
                      <b className="theme_text_color">{`(${detail?.Caliper?.replace(
                        /\\"/g,
                        '"'
                      )})`}</b>{' '}
                      caliper.
                    </Typography>
                  );
                }
              )}
            {applicationData?.ApplicationStatus !== 'Draft' &&
              replacementMethodContent?.map((method, i) => {
                return (
                  <>
                    <Grid
                      key={i}
                      item
                      xs={12}
                      mb={1}
                      mt={{ xs: 2, sm: 'auto' }}
                      container
                      spacing={2}
                      display={'flex'}
                      alignItems={'center'}
                    >
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography color={'#6e759f'} fontWeight={'bold'}>
                          {method?.text}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <RadioGroup
                          name={method?.name}
                          onChange={(e) =>
                            setReplacementMethodData({
                              ...replacementMethodData,
                              vendorToReplace: '',
                              townshipToReplace: '',
                              [method?.name]: e.target.value
                            })
                          }
                          value={replacementMethodData[method?.name]}
                          row
                        >
                          <FormControlLabel
                            value={'yes'}
                            control={<Radio disabled={false} />}
                            label={'Yes'}
                            disabled={
                              (i === 0 &&
                                replacementMethodData?.townshipToReplace ===
                                  'yes') ||
                              (i === 1 &&
                                replacementMethodData?.vendorToReplace ===
                                  'yes')
                            }
                          />
                          <FormControlLabel
                            value={'no'}
                            control={<Radio disabled={false} />}
                            label={'No'}
                            disabled={
                              (i === 0 &&
                                replacementMethodData?.townshipToReplace ===
                                  'yes') ||
                              (i === 1 &&
                                replacementMethodData?.vendorToReplace ===
                                  'yes')
                            }
                          />
                        </RadioGroup>
                      </Grid>
                    </Grid>
                    {replacementMethodData?.vendorToReplace === 'yes' &&
                    i === 0 &&
                    !applicationData?.SelectionOption ? (
                      <ContentOnReplacementMethodChange
                        replacementMethodData={replacementMethodData}
                        replacementFee={replacementFee}
                        setReplacementFee={setReplacementFee}
                        index={i}
                        applicationData={applicationData}
                      />
                    ) : null}
                    {replacementMethodData?.townshipToReplace === 'yes' &&
                    i === 1 &&
                    !applicationData?.SelectionOption ? (
                      <ContentOnReplacementMethodChange
                        replacementMethodData={replacementMethodData}
                        replacementFee={replacementFee}
                        setReplacementFee={setReplacementFee}
                        index={i}
                        applicationData={applicationData}
                      />
                    ) : null}
                  </>
                );
              })}
            {applicationData?.ApplicationStatus !== 'Draft' &&
              !shouldShowEdit(applicationData) && (
                <Grid item xs={12} sm={8} pt={4}>
                  <Grid item>
                    <CustomButtonContained
                      sx={{ width: { xs: '100%', sm: 'auto' } }}
                      onClick={() => setIsOpen(true)}
                      title={' Send Message'}
                    />
                    <CustomButtonContained
                      sx={{
                        ml: { xs: '0', sm: 2 },
                        mt: { xs: 1.5, sm: 'auto' },
                        width: { xs: '100%', sm: 'auto' }
                      }}
                      title={'View Message History'}
                      onClick={() => setSwitchTo('View Message History')}
                    />
                    {replacementFee && (
                      <CustomButtonContained
                        sx={{
                          ml: { xs: '0', sm: 2 },
                          mt: { xs: 1.5, sm: 'auto' },
                          width: { xs: '100%', sm: 'auto' }
                        }}
                        disabled={!replacementFee}
                        title={'Proceed to payment'}
                      />
                    )}
                    <CustomButtonContained
                      sx={{
                        ml: { xs: '0', sm: 2 },
                        mt: { xs: 1.5, sm: 'auto' },
                        width: { xs: '100%', sm: 'auto' }
                      }}
                      title={'View Payment History'}
                      onClick={() => setSwitchTo('View Payment History')}
                    />
                  </Grid>
                </Grid>
              )}
            <Divider sx={{ my: 2 }} />
            <Box display={'flex'} justifyContent={'end'}>
              <CustomButtonOutlined
                title={'Cancel'}
                onClick={() => {
                  if (applicationData?.ApplicationStatus === 'Draft')
                    navigate(`${basePath}/draft-application`);
                  else navigate(`${basePath}/application-list`);
                }}
              />
              {applicationData?.ApplicationStatus !== 'Draft' && (
                <CustomButtonContained
                  title={'Approve'}
                  sx={{ ml: 2 }}
                  disabled={
                    applicationData?.ApplicationStatus === 'Await Vendor' ||
                    shouldShowEdit(applicationData)
                  }
                  onClick={() => setOpenApprovePopup(true)}
                />
              )}
            </Box>
          </>
        </Card>
      </Box>
      <CustomModal
        title={'Send Message'}
        IsOpen={IsOpen}
        setIsOpen={setIsOpen}
        actionButtons={['Cancel', 'Send']}
        loading={loading}
        messageBodyRef={messageBodyRef}
        setLoading={setLoading}
        applicationNumber={applicationData?.ApplicationNumber}
        ApplicationID={applicationData?.ApplicationID}
        applicationDetails={applicationData}
        content={
          <TextField
            className="txtArea"
            label="Message"
            placeholder="Message"
            name="message"
            multiline
            onChange={(e) => handleMessageChange(e)}
            sx={{ width: { xs: '350px', sm: '600px' } }}
            rows={3}
          />
        }
      />
      <CustomModal
        title={'Approve Application?'}
        IsOpen={openApprovePopup}
        setIsOpen={setOpenApprovePopup}
        onSave={() => {
          setOpenApprovePopup(false);
          handleApproveDeny('Approved');
        }}
        actionButtons={['No', 'Yes']}
        loading={loading}
        content={
          <Typography>
            Are you sure you want to approve this application?
          </Typography>
        }
      />
    </>
  );
}

export default EditApplicationForManager;
